import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useAmplitude } from 'react-amplitude-hooks'
import { CourseProvider, LessonProvider, useCourse } from 'contexts'
import { BlockTypes } from 'enum/block-type.enum'
import InformationModal from 'components/ui/modals/information/InformationModal'
import { IEvaluationQuestion } from 'models/evaluation.model'
import * as fromChatActions from 'store/actions/chat.action'
import Chat from 'components/ui/chat/Chat'
import { ButtonVariant } from 'design-system/button/src/Button'
import Button from 'design-system/button'
import {
  AmplitudeEvents,
  AssignedLessonStateEnum,
  CourseStateType,
  SynchronousCourseTypes,
} from 'enum'
import { ILessonModel } from 'models/lesson.model'
import * as fromActions from 'store/actions/courses.action'
import { IMainState } from 'store/states'
import CourseSidebard from 'components/ui/course-sidebar/CourseSidebar'
import { useIntegrations } from 'contexts/IntegrationsContext'
import Loader from 'components/ui/loader/Loader'
import { useSearch } from 'hooks/react.hooks'
import { hasAccessToCourse } from 'helpers/courses/hasAccessToCourse'
import { orderLessons } from 'helpers/courses/order'
import Wrapper from 'components/reusables/wrapper'
import { Sence } from '../../integrations/Sence'
import InfoModal from '../../ui/modals/information/lesson/InfoLessonModal'
import LessonCard from './components/LessonCard'
import useActions from './hooks/useActions'
import PendingActivities from './components/pendingActivities'
import { Container, Buttons } from './styled'

enum nextLessonButtonLabel {
  next = 'next',
  finish = 'finish',
}

const View = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const [origin] = useSearch<'origin'>()
  const {
    course,
    courseProgress,
    loading: courseLoading,
    loadingProgress,
    section,
  } = useSelector(({ courses }: IMainState) => courses)
  const { courseUnreadMessagesCount } = useSelector(
    ({ chat }: IMainState) => chat
  )
  const { permission } = useSelector(({ user }: IMainState) => user)
  const [hasPrevLesson, setHasPrevLesson] = useState(false)
  const mandatoriesRef = useRef<(HTMLElement | null)[]>([])
  const [showChat, setShowChat] = useState(false)
  const { loading: contentLoading } = useSelector(
    ({ lmsLessons }: IMainState) => lmsLessons
  )
  const [onEvaluation, setOnEvaluation] = useState(false)
  const [evaluationData, setEvaluationData] = useState(
    {} as IEvaluationQuestion
  )

  const {
    submit,
    setSubmit,
    currentLesson,
    setCurrentLesson,
    modalData,
    showFinishModal,
    setShowFinishModal,
    pendingActivities,
    setPendingActivities,
    handleGetPrevLesson,
    handleGetNextLesson,
    handleGoToLesson,
    handleFinish,
    handleSendMessage,
    showIncompleteMultimediaModal,
    showIncompleteEvaluationsModal,
    showIncompleteTasksModal,
  } = useActions()

  const { canAnswer } = useCourse()

  const { logEvent } = useAmplitude()
  const setMandatoriesRef = useCallback((array: (HTMLElement | null)[]) => {
    mandatoriesRef.current = array
  }, [])

  useEffect(() => {
    if (currentLesson.id) {
      const isExternal =
        course.synchronousCourse?.synchronousCourseType ===
        SynchronousCourseTypes.external
      logEvent(AmplitudeEvents.lmsLessonStarted, {
        source: JSON.stringify(origin) !== '{}' ? 'widget' : 'module',
        'Course ID': courseId,
        'Type of Platform': '',
        'Requires Application': course.mandatory,
        Sincronicity: isExternal,
        'Lesson ID': currentLesson.id,
        'Number of lesson started': course.lessons.length,
        Section: section,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLesson.id])

  useEffect(() => {
    dispatch(fromActions.Actions.getCourseAndCreateEnrol(+courseId))

    !permission.admin &&
      !permission.superadmin &&
      dispatch({
        type: fromChatActions.GET_COURSE_UNREAD_MESSAGES_COUNT,
        payload: courseId,
      })
  }, [courseId, dispatch, permission.admin, permission.superadmin])

  useEffect(() => {
    return () => setCurrentLesson({})
  }, [setCurrentLesson])

  useEffect(() => {
    showChat &&
      !permission.admin &&
      !permission.superadmin &&
      dispatch({
        type: fromChatActions.GET_COURSE_UNREAD_MESSAGES_COUNT,
        payload: courseId,
      })
  }, [courseId, dispatch, permission.admin, permission.superadmin, showChat])

  const disabled = !!(courseLoading || contentLoading)

  const { currentLessonContent } = useSelector(
    ({ lmsLessons }: IMainState) => lmsLessons
  )

  useEffect(() => {
    if (currentLessonContent.length > 0) {
      const evaluation = currentLessonContent.find(
        (block: any) => block.type === BlockTypes.BlockEvaluation
      ) as IEvaluationQuestion
      if (evaluation && evaluation.name) setEvaluationData(evaluation)
    }
  }, [currentLessonContent, onEvaluation])

  useEffect(() => {
    if (canAnswer && course.id && course.enrol)
      dispatch({
        type: fromActions.COURSES_USER_COURSE_PROGRESS,
        payload: course.id,
      })
  }, [canAnswer, course, dispatch])

  useEffect(() => {
    if (!loadingProgress && Number(courseId) === course.id) {
      const lessons = course.lessons.map((row) => ({ ...row }))
      const { Unstarted: unstartedState, InProgress: inProgressState } =
        AssignedLessonStateEnum
      let auxLessons = lessons[0] as ILessonModel | undefined
      // Existen lecciones en assigned_lessons? (sin importar su estado)
      if (!!courseProgress.length && !currentLesson.id) {
        // Hay alguna lección sin finalizar?
        const unfinished = courseProgress.find(
          ({ state }) => state === unstartedState || state === inProgressState
        ) as any
        if (unfinished) {
          auxLessons = lessons.find(({ id }) => unfinished.lessonId === id)
        } else {
          if (courseProgress.length !== lessons.length) {
            let lastIndex = 0
            // Toma la lección última lección que no existas en assigned_lessons
            lessons.some(({ id }, index) => {
              if (!courseProgress.find(({ lessonId }) => lessonId === id)) {
                lastIndex = index
                return true
              }
            })
            auxLessons = lessons[lastIndex]
          }
        }
      }
      // Si ninguna lección cumple alguna de las condiciones, se toma la primera lección
      auxLessons && !currentLesson.id && setCurrentLesson(auxLessons)
    }
  }, [
    course.id,
    course.lessons,
    courseId,
    courseProgress,
    currentLesson.id,
    loadingProgress,
    setCurrentLesson,
  ])

  const isImparted = course.state === CourseStateType.Imparted
  useEffect(() => {
    const { lessons } = course
    if (!isImparted) return
    const [firstLesson] = lessons
    if (firstLesson && firstLesson.id) setCurrentLesson(firstLesson)
  }, [course, isImparted, setCurrentLesson])

  useEffect(() => {
    if (currentLesson && currentLesson.order) {
      window.scrollTo(0, 0)
      const { lessons } = course
      lessons.map((lesson, index) => (lesson.order = index + 1))
      setHasPrevLesson(currentLesson.order > 1 ? true : false)
    }
  }, [course, courseId, currentLesson])

  const lessonsCount = course.lessons.length

  const nextButton = useMemo(
    () =>
      currentLesson.order < lessonsCount
        ? nextLessonButtonLabel.next
        : nextLessonButtonLabel.finish,
    [lessonsCount, currentLesson.order]
  )

  const hasAccess = useMemo(() => hasAccessToCourse(course), [course])

  const {
    sence: { authenticated: hasSenceSession, loading },
  } = useIntegrations()

  const lessons = useMemo(() => orderLessons(course.lessons || []), [course])

  if (loading) return <Loader overlay color />

  const goHome = () => {
    history.push(`/courses/${courseId}`)
  }

  if (!permission.admin && !hasAccess && !courseLoading)
    return (
      <InformationModal
        show
        text={t(
          `lessons.modals.pendingApproval.message${
            course.directChiefApproval
              ? 'WithChiefDirect'
              : 'WithoutChiefDirect'
          }`
        )}
        acceptButtonText={t('errorPages.buttons.backHome')}
        onAccept={goHome}
        onClose={goHome}
        twoButtons={false}
      />
    )

  return (
    <LessonProvider
      next={handleGetNextLesson}
      submit={submit}
      setSubmit={setSubmit}
    >
      <Container>
        <CourseSidebard
          course={course}
          lessons={lessons}
          chatActive={showChat}
          showChat={(show: boolean) => setShowChat(show)}
          courseProgress={courseProgress}
          currentLesson={currentLesson}
          handleGoToLesson={handleGoToLesson}
          newMessages={courseUnreadMessagesCount}
          onEvaluation={onEvaluation}
          setOnEvaluation={setOnEvaluation}
          evaluationData={evaluationData}
          onIncompleteMultimedia={showIncompleteMultimediaModal}
          onIncompleteEvaluations={showIncompleteEvaluationsModal}
          onIncompleteTasks={showIncompleteTasksModal}
        />
        <Wrapper shadow={true}>
          {showChat ? (
            <Chat courseId={+courseId} onSend={handleSendMessage} />
          ) : (
            <>
              {currentLesson && currentLesson.id > 0 && (
                <LessonCard
                  currentLesson={currentLesson}
                  setMandatoriesRef={setMandatoriesRef}
                  canEdit={canAnswer}
                  onEvaluation={onEvaluation}
                  setOnEvaluation={setOnEvaluation}
                />
              )}
              {!onEvaluation && !course.enrol?.finishDate && (
                <Buttons>
                  <Sence />
                  {hasPrevLesson && (
                    <Button
                      variant={ButtonVariant.Secondary}
                      onClick={handleGetPrevLesson}
                      disabled={!hasPrevLesson || disabled}
                      style={{ float: 'left' }}
                    >
                      {t('commons.buttons.previous')}
                    </Button>
                  )}
                  <Button
                    disabled={
                      disabled ||
                      (nextButton === nextLessonButtonLabel.finish &&
                        !canAnswer)
                    }
                    onClick={() => !disabled && handleGetNextLesson()}
                  >
                    {t(`commons.buttons.${nextButton}`)}
                  </Button>
                </Buttons>
              )}
            </>
          )}
        </Wrapper>
      </Container>
      <InfoModal data={modalData} />
      {pendingActivities && (
        <PendingActivities close={() => setPendingActivities(false)} />
      )}
      <InformationModal
        show={showFinishModal}
        text={t('lessons.modals.finishConfirmation.message')}
        acceptButtonText={t('lessons.modals.buttons.finish')}
        onAccept={() => handleFinish()}
        onClose={() => setShowFinishModal(false)}
        twoButtons={true}
      />
    </LessonProvider>
  )
}

export default () => (
  <CourseProvider>
    <View />
  </CourseProvider>
)
